import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useEffect, useRef } from 'react';

const pillars = [
  {
    icon: 'ri-barcode-line',
    name: 'Processing',
    accent: 'text-accent-400',
    borderTop: 'border-t-accent-500',
    borderLeft: 'border-l-accent-500/40',
    bgLight: 'bg-accent-500/15',
    stat: '42+ centres',
    statLabel: 'across coastal India',
    description: 'Your harvest, cleaned, graded, weighed, vacuum-packed and standardised into a trade-ready SKU — before it ever enters storage.',
    href: '/processing',
  },
  {
    icon: 'ri-building-2-line',
    name: 'Storage',
    accent: 'text-secondary-300',
    borderTop: 'border-t-secondary-400',
    borderLeft: 'border-l-secondary-400/40',
    bgLight: 'bg-secondary-500/15',
    stat: '340+ warehouses',
    statLabel: 'on the network',
    description: 'List idle capacity or find freezing space near your harvest — mapped in real time.',
    href: '/storage',
  },
  {
    icon: 'ri-truck-line',
    name: 'Transport',
    accent: 'text-secondary-300',
    borderTop: 'border-t-secondary-400',
    borderLeft: 'border-l-secondary-400/40',
    bgLight: 'bg-secondary-500/15',
    stat: '2,500+ vehicles',
    statLabel: 'registered',
    description: 'Lease spare space in your reefer van, or move produce affordably across India.',
    href: '/transport',
  },
  {
    icon: 'ri-exchange-line',
    name: 'Wholesale',
    accent: 'text-secondary-300',
    borderTop: 'border-t-secondary-500',
    borderLeft: 'border-l-secondary-500/40',
    bgLight: 'bg-secondary-500/15',
    stat: '8,500+ SKUs',
    statLabel: 'traded monthly',
    description: 'Buy and sell frozen seafood, meat and vegetables at trade scale, with standard specs you can trust.',
    href: '/wholesale',
  },
  {
    icon: 'ri-store-2-line',
    name: 'Retail',
    accent: 'text-accent-400',
    borderTop: 'border-t-accent-500',
    borderLeft: 'border-l-accent-500/40',
    bgLight: 'bg-accent-500/15',
    stat: '12,000+ orders',
    statLabel: 'delivered monthly',
    description: 'Order hygienic, standard-cut frozen food for your home, delivered cold every time.',
    href: '/retail',
  },
];

export default function PillarsSection() {
  const { ref, revealed } = useScrollReveal();
  const sectionRef = useRef<HTMLElement>(null);

  const tryScrollToPillars = () => {
    if (window.location.hash === '#pillars' && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const t = setTimeout(tryScrollToPillars, 120);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onHashChange = () => tryScrollToPillars();
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return (
    <section id="pillars" ref={sectionRef} className="relative py-20 md:py-28 bg-brand-800 scroll-mt-[72px] overflow-hidden">
      {/* Subtle top gradient to blend from section above */}
      <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-brand-700/50 to-transparent"></div>
      {/* Frost texture */}
      <div className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      ></div>

      <div ref={ref} className={`container-page relative z-10 scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block text-xs font-semibold text-accent-400 uppercase tracking-widest mb-4">
            The Platform
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-paper-50 mb-4">
            One Platform, Five Pillars
          </h2>
          <p className="text-brand-200/70 max-w-2xl mx-auto">
            Each pillar operates independently but connects seamlessly — creating unmatched network effects across India&apos;s frozen food supply chain. Processing is where raw harvest becomes standardised product.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6 items-stretch">
          {pillars.map((pillar, idx) => (
            <div
              key={pillar.name}
              className={`group relative bg-brand-700/80 backdrop-blur-sm rounded-2xl p-6 md:p-7 border border-brand-500/30 border-t-4 ${pillar.borderTop} border-l-2 ${pillar.borderLeft} hover:-translate-y-2 hover:bg-brand-700/95 frost-glow transition-all duration-400 cursor-pointer flex flex-col card-animate`}
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${pillar.bgLight} ${pillar.accent} mb-5 animate-icon-float icon-hover-pop flex-shrink-0`}>
                <i className={`${pillar.icon} text-xl`}></i>
              </div>
              <h3 className={`font-heading text-lg font-bold ${pillar.accent} mb-2`}>
                Freshhlock {pillar.name}
              </h3>
              <p className="text-sm text-brand-200/80 leading-relaxed mb-5 flex-1">
                {pillar.description}
              </p>
              <div className="border-t border-brand-500/20 pt-4 mb-4 flex-shrink-0">
                <div className="font-heading text-2xl font-bold text-paper-50 group-hover:text-accent-400 transition-colors duration-300">
                  {pillar.stat}
                </div>
                <div className="text-xs text-brand-300/60">{pillar.statLabel}</div>
              </div>
              <Link
                to={pillar.href}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-400 group-hover:text-accent-300 transition-colors whitespace-nowrap flex-shrink-0"
              >
                Learn more
                <i className="ri-arrow-right-line text-sm transition-transform group-hover:translate-x-1"></i>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}